/**
 * Who reads a saved query.
 *
 * A saved report is not always a report someone opens: a Smart Collection's
 * rule (collections.rule_query_id, migration 637) and a pool's feed
 * (item_pools.feed_query_id, migration 723) are saved reports that something
 * else evaluates. Both FKs are ON DELETE SET NULL, so deleting the report from
 * the Reports library silently turns a smart collection static, or leaves a
 * pool unfed with no record of the rule it was fed by.
 *
 * This is the lookup that makes those readers visible before the delete, and
 * the copy that names them.
 */

import { useCollectionsStore } from '../stores/prioritization/collectionsStore';
import { useItemPoolsStore } from '../stores/prioritization/itemPoolsStore';
import { useSavedReportsStore } from '../stores/prioritization/savedReportsStore';

export interface ReportDependents {
  /** Smart Collections whose rule is this query. */
  collections: { id: string; name: string }[];
  /** Item pools fed by this query (including pools whose feed was severed at lock). */
  pools: { id: string; name: string }[];
}

export function getReportDependents(reportId: string): ReportDependents {
  const collections = useCollectionsStore
    .getState()
    .collections.filter((c) => c.ruleQueryId === reportId)
    .map((c) => ({ id: c.id, name: c.name }));

  const pools = useItemPoolsStore
    .getState()
    .itemPools.filter((p) => p.feedQueryId === reportId)
    .map((p) => ({ id: p.id, name: p.name }));

  return { collections, pools };
}

export function hasReportDependents(d: ReportDependents): boolean {
  return d.collections.length > 0 || d.pools.length > 0;
}

/**
 * The saved query a derived rule was copied from (migration 725), or null when
 * it was written ad hoc. The copy is independent: this is provenance, and the
 * caller's job is to say so rather than imply the two stay in step.
 */
export function getReportOrigin(reportId: string): { id: string; name: string } | null {
  const reports = useSavedReportsStore.getState().reports;
  const sourceId = reports.find((r) => r.id === reportId)?.sourceReportId;
  if (!sourceId) return null;
  const source = reports.find((r) => r.id === sourceId);
  return source ? { id: source.id, name: source.name } : null;
}

/** The rules copied from this query — what an edit here will NOT reach. */
export function getReportCopies(reportId: string): { id: string; name: string }[] {
  return useSavedReportsStore
    .getState()
    .reports.filter((r) => r.sourceReportId === reportId)
    .map((r) => ({ id: r.id, name: r.name }));
}

/**
 * A sentence naming the readers and where to detach them, or null when the
 * query is nobody's rule and can be deleted freely.
 */
export function describeReportDependents(d: ReportDependents): string | null {
  if (!hasReportDependents(d)) return null;

  const parts: string[] = [];
  if (d.collections.length > 0) {
    parts.push(
      d.collections.length === 1
        ? `the Smart Collection “${d.collections[0].name}”`
        : `${d.collections.length} Smart Collections`,
    );
  }
  if (d.pools.length > 0) {
    parts.push(
      d.pools.length === 1
        ? `the item pool “${d.pools[0].name}”`
        : `${d.pools.length} item pools`,
    );
  }

  const readers = parts.join(' and ');
  return `This query is the rule for ${readers}. Remove the rule there first, or ${readers} would stop following it.`;
}
